import { v4 } from "uuid";
import axios from "axios";
import { showNotification } from "@/dentlab/src/store/Central/selectors";
import { supabase } from "@/lib/supabase";
import { Logger } from "@/lib/logger/Logger";
import { StorageBucketsEnum } from "@/dentlab/src/types/enums";
import { CustomFormData, CustomFormFileType } from "../types";
import { ConnectGetOrCreateUserResponse } from "@/dentlab/src/store/Central/client.store";
import { base64ToBlob, sanitizeFileName } from "@/dentlab/src/lib/utils/utils-functions";

export async function uploadJobRequestFiles(
		customFormData: CustomFormData
	): Promise<string[]> {
		const filePaths: string[] = [];

		for (const fieldId in customFormData) {
			const fieldData = customFormData[fieldId];

			if (
				fieldData.fieldType === "file" &&
				Array.isArray(fieldData.fieldValue) &&
				fieldData.fieldValue.length > 0
			) {
				for (const file of fieldData.fieldValue) {
					// Check if file object has the expected properties of FileType
					if (
						typeof file === "object" &&
						file !== null &&
						"name" in file &&
						"data" in file &&
						"type" in file &&
						"size" in file
					) {
						const fileObj = file as CustomFormFileType;
						const filePath = `${v4()}/${sanitizeFileName(fileObj.name)}`;

						let fileData: Blob;
						if (
							typeof fileObj.data === "string" &&
							fileObj.data.startsWith("data:")
						) {
							// It's a data URL, convert to Blob using utility function
							fileData = base64ToBlob(fileObj.data, fileObj.type);
						} else if (
							typeof fileObj.data === "object" &&
							fileObj.data !== null
						) {
							fileData = new Blob([fileObj.data], {
								type: fileObj.type,
							});
						} else {
							Logger.error("Unsupported file data format");
							showNotification({
								message: "Dateityp nicht unterstützt",
								type: "error",
							});
							return [];
						}
						const { error } = await supabase.storage
							.from(StorageBucketsEnum.V1)
							.upload(filePath, fileData, {
								contentType: fileObj.type,
							});

						if (error) {
							Logger.error(error, {}, "Error uploading file");
							showNotification({
								message: "Fehler beim Hochladen der Datei",
								type: "error",
							});
							return [];
						}

						filePaths.push(filePath);
					} else if (typeof file === "string") {
						filePaths.push(file);
					}
				}
			}
		}

		return filePaths;
	};

export function stripFileData(customFormData: CustomFormData): CustomFormData {
	return Object.fromEntries(
		Object.entries(customFormData).map(([fieldId, field]) => [
			fieldId,
			field.fieldType === "file" && Array.isArray(field.fieldValue)
				? {
						...field,
						fieldValue: (field.fieldValue as CustomFormFileType[]).map(
							(file) =>
								typeof file === "object" && file !== null
									? { name: file.name, type: file.type, size: file.size }
									: file
						),
					}
				: field,
		])
	);
}

export async function getOrCreateVendorOrg(email: string): Promise<string | null> {
	try {
		const {
			data,
			error: connectError,
		}: ConnectGetOrCreateUserResponse = await axios({
			url: `${import.meta.env.VITE_PDF_MICROSERVICE_URL}/connect/get-or-create-user`,
			method: "POST",
			data: {
				email: email.toLowerCase(),
				org_type: "lab",
			},
		});

		if (connectError || !data?.data?.organizationId) {
			throw new Error("Fehler beim Abrufen der Lieferanten-Organisation");
		}

		return data.data.organizationId;
	} catch (error) {
		Logger.error(error, {}, "Error getting vendor organization");
		showNotification({
			message: "Fehler beim Abrufen der Lieferanten-Organisation",
			type: "error",
		});
		return null;
	}
}
